import TopTenIcon from 'assets/Icons/TopTenIcon';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';

import { ShouldRender } from '@/components/shared/basic/ShouldRender';

import type { IBookShop } from '@/utils/interfaces/BooksShop';
import { formatPrice } from '@/utils/Product';

interface BookCardProps {
  book: IBookShop;
  index: number;
  type?: string;
}

const BookCard = ({ book, index, type }: BookCardProps) => {
  const item = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        delay: index * 0.05,
      },
    },
  };
  const getHref = () => {
    if (type === 'oranges' || book.type === 'paperBack') {
      return `/oranges/${book.slug}-${book.id}`;
    } else if (type === 'bundles' || book.type === 'bundle') {
      return `/bundles/${book.slug}-${book.id}/`;
    } else if (book.tag === 'PRE ORDER') {
      return `/books/${book.slug}-${book.id}/`;
    } else if (book.isFree) {
      return `/books/${book.slug}-${book.id}/?short=true`;
    } else {
      return `/books/${book.slug}-${book.id}/`;
    }
  };

  return (
    <Link href={getHref()} passHref>
      <motion.div
        className={clsx(
          'w-full  lg:p-6 overflow-hidden transition-all rounded-[6px] cursor-pointer duration-300 group',
        )}
        variants={item}
      >
        <div className='flex justify-center'>
          <div className='relative w-[168px] h-[238px] group-hover:scale-[1.02]'>
            <Image
              src={book.coverImage}
              fill
              alt={book.title}
              className='object-cover rounded-md transition-transform duration-500'
            />

            <ShouldRender check={book.tag && !book.topTen}>
              <div
                style={{
                  boxShadow: '0px 4px 4px 0px #00000040',
                }}
                className={clsx(
                  'absolute top-0 right-0 px-[5px]  py-1 text-[10px] font-bold',
                  {
                    'bg-white text-black rounded-bl-[4px] rounded-tr-[4px]':
                      book.tag?.toLowerCase() === 'bestseller',
                    'bg-[#DC2626] text-white':
                      book.tag?.toLowerCase() === 'new' ||
                      book.tag?.toLowerCase() === 'new release',
                    'bg-[#280845] text-white rounded-bl-[4px] rounded-tr-[4px]':
                      book.tag?.toLowerCase() === 'pre order' ||
                      book.tag?.toLowerCase() === 'coming soon',
                    'bg-primary text-white': ![
                      'bestseller',
                      'new',
                      'new release',
                      'pre order',
                      'coming soon',
                      'free',
                    ].includes(book.tag?.toLowerCase() || ''),
                  },
                )}
              >
                {book.tag}
              </div>
            </ShouldRender>

            <ShouldRender check={book.topTen}>
              <div
                style={{
                  clipPath: 'polygon(0 0, 100% 0%, 100% 100%, 0 83%)',
                  backgroundColor: '#FF5A1F',
                  boxShadow: '0px 4px 4px 0px #00000040',
                }}
                className='absolute top-0 right-0 px-1 pt-1 pb-[10px] md:pb-[8px] text-white font-bold text-[10px] rounded-tr-md'
              >
                <div className='flex flex-col items-center leading-tight'>
                  <TopTenIcon />
                </div>
              </div>
            </ShouldRender>
          </div>
        </div>

        <div className='p-0 '>
          {/* <div className='flex justify-center'>
            <div className='flex items-center justify-center my-3 lg:my-5 py-0.5 rounded-[2px] px-2 bg-[#FFFFFF1A] w-min space-x-1 '>
              <span className='text-sm font-semibold text-white'>
                {roundToNearestHalf(book.rating)}
              </span>
              <StarFilled className='text-[#FFFB12] text-sm prtext-fadedWhite  flex flex-col gap-y-4 md:gap-y-6  lg:w-1/2 xl:w-3/5-1' />

              <Divider
                type='vertical'
                className='max-h-[12px] mx-4  translate-y-[1px] bg-[#9F9EA6] text-sm '
              />
              <span className='text-sm font-medium pl-1 text-white'>
                {book.reviews}
              </span>
            </div>
          </div> */}

          <div className='text-center text-[10px]  mt-3 lg:mt-5 md:text-xs text-primary font-medium tracking-wider  uppercase '>
            {book.category}
          </div>

          <div className=' pt-1.5'>
            <h3 className='font-semibold text-white text-center mb-1 text-[13px] md:text-base lg:text-lg truncate'>
              {book.title}
            </h3>

            <p className='text-[10px] md:text-xs font-light text-textLightGray text-center mb-3 mt-1 truncate capitalize'>
              {book.author}
            </p>

            {book.isFree ? (
              <div className='flex justify-center'>
                <button className='px-2 py-0.5 md:py-1 text-xs md:text-sm font-medium bg-cartBgColor  transition-colors border rounded-[4px] border-white text-white'>
                  Free To Read
                </button>
              </div>
            ) : book.is_available ? (
              <div className='flex items-center justify-center'>
                <span className='text-base md:text-lg font-semibold text-white'>
                  {book.currency}
                  {formatPrice(book.price)}
                </span>
                {Boolean(book.originalPrice && Number(book.originalPrice) > Number(book.price)) && (
                  <span className='ml-2 text-sm text-[#9F9EA6] font-medium line-through'>
                    {book.currency}
                    {formatPrice(book.originalPrice)}
                  </span>
                )}
                {Boolean(book.discount && Number(book.discount) > 0) && (
                  <span className='ml-1 text-sm text-[#4ADE80]'>
                    {book.discount}%
                  </span>
                )}
              </div>
            ) : (
              <div className='flex justify-center'>
                <button className='px-2 py-0.5 md:py-1 text-xs font-medium bg-grayZl-700  transition-colors border rounded-[4px] border-grayZl-600 text-white cursor-not-allowed'>
                  Currently Unavailable
                </button>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </Link>
  );
};

export default BookCard;
